import React, { useCallback, useState } from "react";
import {
  Backdrop,
  Box,
  Divider,
  Fade,
  IconButton,
  Modal,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch } from "react-redux";
import _ from "lodash";
import CustomButton from "../../common/button/CustomButton";
import Confirmation from "../confirmation-modal/Confirmation";
import theme from "../../../theme";
import { VIRTUAL_LOCKER_ITEM_STATUS_CHANGE } from "../../../actions/VirtualLockerAction";
import {
  SenderLabel,
  SenderValue,
  StyledNoteModalBox,
  StyledNoteModalContent,
  StyledNoteModalHeader,
  StyledNoteModalTitle,
  StyledSpan,
} from "../../list-item/style";
import { formatName } from "../../../utils/common";

const DETAIL_ROW_SX = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: 1,
  py: 0.75,
  flexWrap: "wrap",
};

function DetailRow({ label, value }) {
  if (value == null || value === "" || value === "—") return null;
  return (
    <>
      <Box sx={DETAIL_ROW_SX}>
        <SenderLabel>{label}</SenderLabel>
        <SenderValue sx={{ textAlign: { xs: "left", sm: "right" } }}>{value}</SenderValue>
      </Box>
      <Divider sx={{ borderColor: "#e8f0fb" }} />
    </>
  );
}

export default function ViewLockerItemModal({
  open,
  onClose,
  item,
  getItemLocker,
  isLockerInactive = false,
}) {
  const dispatch = useDispatch();
  const [confirmDoNotShip, setConfirmDoNotShip] = useState(false);
  const [previewSrc, setPreviewSrc] = useState(null);

  const status = String(item?.status ?? "").toLowerCase();
  const images = Array.isArray(item?.images) ? item.images.filter(Boolean) : [];
  const canDoNotShip = !isLockerInactive && !["rejected", "delivered", "approved", "on_hold", "shopping_in_progress"].includes(status);

  const handleDoNotShip = useCallback(() => {
    if (!item?.itemId) return;
    dispatch({
      type: VIRTUAL_LOCKER_ITEM_STATUS_CHANGE,
      payload: {
        id: item.itemId,
        status: "rejected",
        onSuccess: () => {
          getItemLocker?.({});
          onClose?.();
        },
      },
    });
  }, [dispatch, getItemLocker, item?.itemId, onClose]);

  if (!item) return null;

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <StyledNoteModalBox sx={{ width: { xs: "94vw", sm: 560 }, maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
          <StyledNoteModalHeader>
            <StyledNoteModalTitle>View Item</StyledNoteModalTitle>
            <IconButton onClick={onClose} sx={{ color: "#fff", p: 0 }}>
              <CloseIcon />
            </IconButton>
          </StyledNoteModalHeader>

          <StyledNoteModalContent sx={{ p: "20px 24px", overflowY: "auto", flex: 1 }}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5, color: "#0B2373" }}>
              {item.product}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              Status: {formatName(status || "pending")}
              {item.quantity != null ? ` · Qty × ${item.quantity}` : ""}
            </Typography>

            <Box sx={{ mb: 2 }}>
              <DetailRow label="Order Type" value={formatName(item.orderType)} />
              <DetailRow
                label={`Sender (${formatName(item.senderType)})`}
                value={
                  item.createUser?.name ? (
                    <>
                      <StyledSpan>{item.createUser.name}</StyledSpan>
                      {item.createUser?.phone_number ? ` (${item.createUser.phone_number})` : ""}
                    </>
                  ) : (
                    "—"
                  )
                }
              />
              <DetailRow label="Created On" value={item.createdAt} />
              <DetailRow label="Exp. Delivery" value={item.expDelivery || "—"} />
              {item.orderType === "self_drop" && (
                <DetailRow label="Preferred Drop Date" value={item.preferredDropDate || "—"} />
              )}
              {item.note && <DetailRow label="Note" value={item.note} />}
            </Box>

            {images.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                  Product Images
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {images.map((src, index) => (
                    <Box
                      key={index}
                      component="button"
                      type="button"
                      onClick={() => setPreviewSrc(src)}
                      sx={{
                        border: "1px solid #d0e4f7",
                        borderRadius: "8px",
                        overflow: "hidden",
                        p: 0,
                        cursor: "pointer",
                        background: "transparent",
                      }}
                    >
                      <Box
                        component="img"
                        src={src}
                        alt={`Product ${index + 1}`}
                        sx={{ width: 88, height: 80, objectFit: "cover", display: "block" }}
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {canDoNotShip && (
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                <CustomButton
                  padding="10px 24px"
                  height="40px"
                  fontSize={theme.typography.small.fontSize}
                  btnLabel="Do not ship"
                  btntype="danger"
                  onClick={() => setConfirmDoNotShip(true)}
                />
              </Box>
            )}
          </StyledNoteModalContent>
        </StyledNoteModalBox>
      </Modal>

      <Confirmation
        open={confirmDoNotShip}
        handleClose={() => setConfirmDoNotShip(false)}
        heading="Do not ship"
        message={`Mark "${item.product}" as do not ship? This item will not be included in shipment.`}
        confirmButtonLabel="Confirm"
        type="delete"
        onConfirm={handleDoNotShip}
      />

      <Modal
        open={!!previewSrc}
        onClose={() => setPreviewSrc(null)}
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 300, style: { background: "rgba(10,20,50,0.9)" } } }}
      >
        <Fade in={!!previewSrc}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              outline: "none",
            }}
          >
            <Box
              component="img"
              src={previewSrc || ""}
              alt="Full product"
              sx={{
                maxWidth: "88vw",
                maxHeight: "82vh",
                borderRadius: "12px",
                boxShadow: "0 8px 40px rgba(0,0,0,0.6)",
                objectFit: "contain",
                display: "block",
              }}
            />
          </Box>
        </Fade>
      </Modal>
    </>
  );
}
